import React from 'react';
import { CollectionList } from 'types/interfaces/collectionList';
import { AssetUrls } from 'config/assets';
import { normalizeLanguageCode } from 'constants/languages';
import { FinishSetCell } from 'components/FinishSetCell';
import { CardFinish, getAvailableFinishes, wantAcceptedFinishes, wantRenderFinish } from 'utils/finishes';

// Valid condition values that the backend accepts
/**
 * What the finish helpers read off a want row: the accepted finishes and the
 * card's finish source. Structural, so both a full CollectionList and the lean
 * WantListRow (fields=list, Asana 1219915395102217) satisfy it.
 */
export type WantFinishRow = Pick<CollectionList, 'acceptFoil' | 'acceptNonFoil' | 'acceptEtched'> & {
  card?: Parameters<typeof getAvailableFinishes>[0];
};

export const VALID_CONDITIONS =['NEAR_MINT', 'SLIGHTLY_PLAYED', 'MODERATELY_PLAYED', 'HEAVILY_PLAYED', 'DAMAGED'];

// Sort options for dropdown
export const SORT_OPTIONS = [
  { value: 'created_at-desc', label: 'Date Added' },
  { value: 'updated_at-desc', label: 'Date Updated' },
  { value: 'reference_price-desc', label: 'Price: High to Low' },
  { value: 'reference_price-asc', label: 'Price: Low to High' },
  { value: 'card_name-asc', label: 'Name: A-Z' },
  { value: 'card_name-desc', label: 'Name: Z-A' },
  // Bucket sorts: the response carries groups[] and each row a groupKey.
  { value: 'color_identity-asc', label: 'Color' },
  { value: 'set_released-asc', label: 'Set (Oldest First)' },
  { value: 'set_released-desc', label: 'Set (Newest First)' },
];

// View options for dropdown
export const VIEW_OPTIONS: { value: 'images' | 'list'; label: string; icon: string }[] = [
  { value: 'images', label: 'Images', icon: AssetUrls.icons.controls.images },
  { value: 'list', label: 'List', icon: AssetUrls.icons.controls.list },
];

// Get card image URL with fallback. scryfallID is checked first because the
// public want list backend swaps it (along with displayPrice) when the
// "Affordable only" filter picks a cheaper printing — imageUris is a Json?
// column that's frequently null, so trusting it first leaves the image stale
// on swap while the price updates.
export const getCardImageUrl = (item: CollectionList): string => {
  const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;

  if (item.card?.scryfallID) return AssetUrls.cards.getCardImage(item.card.scryfallID, 'normal');
  if (item.card?.imageUris?.normal) return item.card.imageUris.normal;
  if (item.card?.imageUris?.small) return item.card.imageUris.small;
  if (item.card?.imageUris?.large) return item.card.imageUris.large;
  if (item.card?.normal_fallbacks?.[0]) return item.card.normal_fallbacks[0];

  return defaultImage;
};

// Helper to get a valid condition value
export const getValidCondition = (condition?: string): string => {
  return condition && VALID_CONDITIONS.includes(condition) ? condition : 'NEAR_MINT';
};

// The language and printing a staged copy of a want row sends, read from the
// row (backend getPublicWantList). defaultLanguage only considers languages the
// printing exists in; null means the row has no sendable version, so nothing is
// staged. defaultLanguageCard is the same printing (oracle + set + number) in
// defaultLanguage when that differs from the pictured card; its id is staged,
// since displayPrice is priced from that tuple and the debit paths accept a
// language variant of a want's specific printing (Asana 1219267077634304).
export interface StagedPrinting {
  language: string;
  cardId: number | undefined;
  scryfallId: string | undefined;
  /** Image of the staged printing; undefined keeps the row's own image. */
  imageUrl: string | undefined;
}

/** The backend's resolved language for a want; carried by want rows and Activity want items. */
export interface ResolvedWantLanguage {
  defaultLanguage?: string | null;
  defaultLanguageCard?: { id: number; scryfallId: string; imageUri: string | null; lang: string } | null;
}

export const stagedPrintingFromResolved = (
  resolved: ResolvedWantLanguage,
  pickedCardId: number | undefined,
): StagedPrinting | null => {
  if (!resolved.defaultLanguage) return null;
  const variant = resolved.defaultLanguageCard;
  return {
    language: normalizeLanguageCode(resolved.defaultLanguage),
    cardId: variant?.id ?? pickedCardId,
    scryfallId: variant?.scryfallId,
    imageUrl: variant ? variant.imageUri || AssetUrls.cards.getCardImage(variant.scryfallId, 'normal') : undefined,
  };
};

export const stagedPrintingFromRow = (
  item: CollectionList,
  pickedCardId: number | undefined,
): StagedPrinting | null => stagedPrintingFromResolved(item, pickedCardId);

// The finish a want row's art renders in (Decision 12): the first finish the
// PRINTING has and the want accepts (non-foil when possible, the single
// offerable finish when there is one, else foil). A foil+etched want on an
// etched-only printing therefore renders etched. Without a declared finish set
// on the card the accepted set alone decides (wantRenderFinish).
export const wantArtFinish = (item: WantFinishRow): CardFinish => {
  const card = item.card;
  if (card && Array.isArray(card.finishes)) {
    const accepted = wantAcceptedFinishes(item);
    const offerable = getAvailableFinishes(card).filter((f) => accepted.includes(f));
    if (offerable.length > 0) {
      return offerable.includes('nonfoil') ? 'nonfoil' : offerable.length === 1 ? offerable[0] : 'foil';
    }
  }
  return wantRenderFinish(item);
};

// Legacy two-value gate, kept for the surfaces Track D has not migrated yet
export const shouldShowFoil = (item: WantFinishRow): boolean => wantArtFinish(item) !== 'nonfoil';

// Helper to format conditions for list view display
export const formatConditions = (acceptableConditions?: string[]): string => {
  if (!acceptableConditions || acceptableConditions.length === 0) return 'Any';
  const conditionMap: Record<string, string> = {
    'NEAR_MINT': 'NM', 'SLIGHTLY_PLAYED': 'LP', 'MODERATELY_PLAYED': 'MP',
    'HEAVILY_PLAYED': 'HP', 'DAMAGED': 'DMG',
  };
  const mapped = acceptableConditions.map(cond => conditionMap[cond] || cond);
  if (mapped.length >= 4 && mapped.includes('NM') && mapped.includes('LP') && mapped.includes('MP') && mapped.includes('HP')) {
    return 'Any';
  }
  return mapped.join(', ');
};

// The want row's finish cell (Design Pass D-5): one mark plus +n, canonical
// order, "Any" for all three; an accepted finish the card lacks is never counted.
export const formatFoilPreference = (
  want: WantFinishRow
): React.ReactNode => React.createElement(FinishSetCell, { want, card: want.card ?? null });

